<script module>
  export { default as layout } from '../components/Layout.svelte'
</script>

<script lang="ts">
  import { faArrowRight, faCalendar, faLandmark, faLanguage, faNewspaper } from '@fortawesome/free-solid-svg-icons'
  import FeatureCard from '../components/FeatureCard.svelte'
  import ButtonLink from '../components/ButtonLink.svelte'
  import MirroredIcon from '../components/MirroredIcon.svelte'
  import LanguageButtonLinks from '../components/LanguageButtonLinks.svelte'
  import PageCard from '../components/PageCard.svelte'
  import SectionHeading from '../components/SectionHeading.svelte'
  import StructuredData from '../components/StructuredData.svelte'
  import { getI18n } from '../i18n'
  import type { SerializedPage } from '../types'

  let { highlightedPages, structuredData }: { highlightedPages: SerializedPage[]; structuredData: Record<string, unknown> } = $props()

  const i18n = getI18n()
</script>

<StructuredData data={structuredData} />

<SectionHeading eyebrow="Visit Augsburg">{i18n.t('home.title')}</SectionHeading>
<p class="intro">{i18n.t('home.intro')}</p>
<div class="feature-cards">
  <FeatureCard
    variant="sightseeing"
    icon={faLandmark}
    eyebrow={i18n.t('home.sightseeing.eyebrow')}
    title={i18n.t('home.sightseeing.title')}
    lead={i18n.t('home.sightseeing.lead')}
  >
    <ButtonLink href={i18n.path('/pages/sightseeing')} variant="button-brand-outline">
      {i18n.t('home.sightseeing.action')}
      {#snippet end()}<MirroredIcon icon={faArrowRight} />{/snippet}
    </ButtonLink>
  </FeatureCard>

  <FeatureCard
    variant="events"
    icon={faCalendar}
    eyebrow={i18n.t('home.events.eyebrow')}
    title={i18n.t('home.events.title')}
    lead={i18n.t('home.events.lead')}
  >
    <ButtonLink href={i18n.path('/pages/events')} variant="button-warning-outline">
      {i18n.t('home.events.action')}
      {#snippet end()}<MirroredIcon icon={faArrowRight} />{/snippet}
    </ButtonLink>
  </FeatureCard>

  <FeatureCard
    variant="blog"
    icon={faNewspaper}
    eyebrow={i18n.t('home.blog.eyebrow')}
    title={i18n.t('home.blog.title')}
    lead={i18n.t('home.blog.lead')}
  >
    <ButtonLink href={i18n.path('/pages/blog')} variant="button-success-outline">
      {i18n.t('home.blog.action')}
      {#snippet end()}<MirroredIcon icon={faArrowRight} />{/snippet}
    </ButtonLink>
  </FeatureCard>

  <FeatureCard
    variant="language"
    icon={faLanguage}
    eyebrow={i18n.t('home.language.eyebrow')}
    title={i18n.t('home.language.title')}
    lead={i18n.t('home.language.lead')}
  >
    <div class="language-badges">
      <LanguageButtonLinks />
    </div>
  </FeatureCard>
</div>

{#if highlightedPages.length > 0}
  <SectionHeading eyebrow={i18n.t('home.highlights.eyebrow')} level="h2">{i18n.t('home.highlights.title')}</SectionHeading>
  <p class="highlights-lead">{i18n.t('home.highlights.lead')}</p>
  <div class="highlights-grid">
    {#each highlightedPages as page (page.id)}
      <PageCard {page} category={page.category} />
    {/each}
  </div>
{/if}

<style>
  .intro {
    color: var(--color-text-muted);
    max-width: 640px;
  }

  .highlights-lead {
    color: var(--color-text-muted);
  }

  .highlights-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-9);
    margin-bottom: var(--space-9);

    @media (--mobile) {
      grid-template-columns: 1fr;
    }
  }

  .feature-cards {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-7);
    padding: var(--space-9) 0;

    @media (--mobile) {
      grid-template-columns: 1fr;
    }
  }

  .language-badges {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-4);
  }
</style>
